import { Injectable, inject } from '@angular/core';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { Categories } from '@iris/modules/projects/models/categories';
import {
  routeToEditPage,
  routeToMembersPage,
  routeToPermissionRequestsPage,
} from '@iris/modules/projects/details/iris-project-details.route-pathes';
import {
  routeToMailboxIntegrationPage,
  routeToProductsLinkingPage,
  routeToDropboxIntegrationPage,
  routeToDmsSettingsPage,
  routeToSharedEmailClient,
  routeToFormsImportDataPage,
  routeToFormsCopyDataToProjectsPage,
  routeToMailboxIntegrationLogsPage,
} from '@iris/modules/projects/details/settings/settings.route-paths';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class IrisProjectsRoutingService {
  private readonly globalSandbox = inject(IrisGlobalSandbox);

  private get baseCommands(): unknown[] {
    const appAlias = this.globalSandbox.appAlias();
    return ['/', appAlias, 'projects'];
  }

  /**
   * Helps to build a link starting from the base path '/:appAlias/projects'
   * @param commands Commands after '/:appAlias/projects'
   */
  private buildLink(commands: unknown[]): unknown[] {
    const link = [...this.baseCommands];
    link.push(...commands);
    return link;
  }

  getLinkToProjectList(category: Categories): unknown[] {
    const commands: unknown[] = [];

    if (category) {
      commands.push(category);
    }

    return this.buildLink(commands);
  }

  getLinkToCreateProjectPage(): unknown[] {
    return this.buildLink(['new']);
  }

  getLinkToProjectView(projectId: number): unknown[] {
    return this.buildLink([projectId]);
  }

  getLinkToProjectEdit(projectId: number): unknown[] {
    return this.buildLink([projectId, routeToEditPage]);
  }

  getLinkToProjectMembers(projectId: number): unknown[] {
    return this.buildLink([projectId, routeToMembersPage]);
  }

  getLinkToProjectPermissionRequests(projectId: number): unknown[] {
    return this.buildLink([projectId, routeToPermissionRequestsPage]);
  }

  getLinkToProjectMailboxIntegration(projectId: number): unknown[] {
    const base = this.getLinkToProjectEdit(projectId);
    base.push(routeToMailboxIntegrationPage);
    return base;
  }

  getLinkToProjectMailboxIntegrationLogs(projectId: number): unknown[] {
    const base = this.getLinkToProjectEdit(projectId);
    base.push(routeToMailboxIntegrationLogsPage);
    return base;
  }

  getLinkToProjectDmsSettings(projectId: number): unknown[] {
    const base = this.getLinkToProjectEdit(projectId);
    base.push(routeToDmsSettingsPage);
    return base;
  }

  getLinkToProjectProductsLinking(projectId: number, configId: number): unknown[] {
    const base = this.getLinkToProjectEdit(projectId);
    base.push(routeToProductsLinkingPage, configId);
    return base;
  }

  getLinkToProjectFormDataImport(projectId: number): unknown[] {
    const base = this.getLinkToProjectEdit(projectId);
    base.push(routeToFormsImportDataPage);
    return base;
  }

  getLinkToProjectFormDataCopyToProjects(projectId: number): unknown[] {
    const base = this.getLinkToProjectEdit(projectId);
    base.push(routeToFormsCopyDataToProjectsPage);
    return base;
  }

  getLinkToProjectDropboxIntegration(projectId: number): unknown[] {
    const base = this.getLinkToProjectEdit(projectId);
    base.push(routeToDropboxIntegrationPage);
    return base;
  }

  getLinkToProjectSharedEmailClient(projectId: number): unknown[] {
    const base = this.getLinkToProjectEdit(projectId);
    base.push(routeToSharedEmailClient);
    return base;
  }

  navigateToProjectView(router: Router, projectId: number): void {
    void router.navigate(this.getLinkToProjectView(projectId));
  }
}
